/**
 * Fairness Plots — aligned with Python backend fairness analysis.
 * Accepts data in the format produced by the MicroEAI fairness script:
 * [{ attribute, coverage, status, audit_status, avg_A, avg_B, disparity, confidence, sensitivity }, ...]
 */
/* eslint-disable prettier/prettier */
import React from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
  Cell
} from 'recharts';
import Typography from '@mui/material/Typography';

import './BiasAggregateSummaryPanel.css';
import './FairnessPlotsSection.css';

const CHART_COLORS = {
  complianceBar: '#093f88',
  auditBar: '#ce3a00',
  fair: '#008c46',
  monitor: '#f59e0b',
  unfair: '#ce3a00',
  groupA: '#093f88',
  groupB: '#ffb300'
};

// Data shape from Python backend fairness script
export interface FairnessResultRow {
  attribute: string;
  coverage: string;
  status: string;
  audit_status: string;
  avg_A: number | null;
  avg_B: number | null;
  disparity: number | null;
  confidence: number;
  sensitivity: number;
}

interface FairnessPlotsSectionProps {
  data: FairnessResultRow[];
  /** Default compact height for charts (px). */
  height?: number;
  /** When false, do not render FairnessSummary here (e.g. rendered at top of parent). */
  showSummary?: boolean;
}

/** Collections sometimes send metrics as strings; Recharts tooltips may pass non-numbers. */
function finiteNumber(v: unknown, fallback = 0): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function fmtFixed(v: unknown, digits: number): string {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return (0).toFixed(digits);
  return n.toFixed(digits);
}

function parseFinitePair(a: unknown, b: unknown): { avgA: number; avgB: number } | null {
  if (a == null || a === '' || b == null || b === '') return null;
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isFinite(na) || !Number.isFinite(nb)) return null;
  return { avgA: na, avgB: nb };
}

// Plot 1: What Needs Review? — horizontal bar of attributes vs disparity
const WhatNeedsReviewChart = ({
  data,
  height = 320,
  emptyMessage = 'No fairness issues to review.'
}: {
  data: FairnessResultRow[];
  height?: number;
  emptyMessage?: string;
}) => {
  const flagged = data
    .filter(r => ['UNFAIR', 'MONITOR', 'PARTIAL'].includes(r.status))
    .sort((a, b) => finiteNumber(a.disparity) - finiteNumber(b.disparity));

  if (flagged.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height }}>
        <p className="text-muted mb-0">{emptyMessage}</p>
      </div>
    );
  }

  const chartData = flagged.map(r => ({
    attribute: r.attribute,
    disparity: finiteNumber(r.disparity)
  }));

  const getBarColor = (entry: { disparity: number }) => {
    if (entry.disparity > 0.3) return CHART_COLORS.unfair;
    if (entry.disparity > 0.15) return CHART_COLORS.monitor;
    return CHART_COLORS.fair;
  };

  const compact = height <= 240;
  const margin = compact ? { top: 4, right: 28, left: 72, bottom: 16 } : { top: 10, right: 40, left: 100, bottom: 30 };

  return (
    <div style={{ width: '100%' }}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart layout="vertical" data={chartData} margin={margin}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
          <XAxis
            type="number"
            name="Disparity"
            tick={{ fill: '#64748b', fontSize: compact ? 10 : 12 }}
            axisLine={{ stroke: '#e2e8f0' }}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="attribute"
            width={compact ? 68 : 90}
            tick={{ fill: '#64748b', fontSize: compact ? 9 : 11, fontWeight: 500 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              padding: '12px 16px'
            }}
            formatter={(value: unknown) => [fmtFixed(value, 3), 'Disparity']}
            labelFormatter={label => `Attribute: ${label}`}
          />
          <Bar dataKey="disparity" radius={[0, 8, 8, 0]} maxBarSize={24}>
            {chartData.map((entry, index) => (
              <Cell key={index} fill={getBarColor(entry)} />
            ))}
            <LabelList
              dataKey="disparity"
              position="right"
              formatter={(v: unknown) => fmtFixed(v, 2)}
              style={{ fontSize: 10 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <p style={{ fontSize: 10, color: '#64748b', marginTop: 4, marginBottom: 0, paddingLeft: 4 }}>
        These attributes show imbalance between groups. Review and decide.
      </p>
    </div>
  );
};

// Plot 2: Should I Act? — Group A vs Group B for a single attribute
const ShouldIActChart = ({
  row,
  height = 320,
  emptyMessage = 'No group comparison data.'
}: {
  row: FairnessResultRow;
  height?: number;
  emptyMessage?: string;
}) => {
  const pair = parseFinitePair(row.avg_A, row.avg_B);
  if (!pair) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height }}>
        <p className="text-muted mb-0">{emptyMessage}</p>
      </div>
    );
  }
  const { avgA, avgB } = pair;

  const chartData = [
    { group: 'Group A', value: avgA },
    { group: 'Group B', value: avgB }
  ];

  const level = row.status === 'UNFAIR' ? 'HIGH' : row.status === 'MONITOR' ? 'MEDIUM' : 'LOW';
  const action = row.status === 'UNFAIR' ? 'REVIEW' : row.status === 'MONITOR' ? 'MONITOR' : 'OK';
  const compact = height <= 220;
  const margin = compact ? { top: 8, right: 8, left: 4, bottom: 12 } : { top: 20, right: 20, left: 10, bottom: 80 };

  return (
    <div style={{ width: '100%' }}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={chartData} margin={margin}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis
            dataKey="group"
            tick={{ fill: '#64748b', fontSize: 12 }}
            axisLine={{ stroke: '#e2e8f0' }}
            tickLine={false}
          />
          <YAxis tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} />
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              padding: '12px 16px'
            }}
          />
          <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={80}>
            <Cell fill={CHART_COLORS.groupA} />
            <Cell fill={CHART_COLORS.groupB} />
            <LabelList
              dataKey="value"
              position="top"
              formatter={(v: unknown) => fmtFixed(v, 2)}
              style={{ fontSize: 11 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div
        style={{
          fontSize: 11,
          color: '#334155',
          marginTop: 8,
          padding: '8px 12px',
          backgroundColor: 'rgba(248,250,252,0.9)',
          borderRadius: 8,
          border: '1px solid #e2e8f0'
        }}
      >
        <div>
          <strong>Difference (Disparity):</strong> {fmtFixed(row.disparity, 2)} → {level} risk
        </div>
        <div>
          <strong>Confidence (Data):</strong> {fmtFixed(row.confidence, 2)}
        </div>
        <div>
          <strong>Sensitivity (Model):</strong> {fmtFixed(row.sensitivity, 2)}
        </div>
        <div style={{ marginTop: 6 }}>
          <strong>Recommended action:</strong> {action}
        </div>
      </div>
    </div>
  );
};

// Plot 3: Final Fairness Posture — Compliance vs Audit (Review Required, Resolved, Excluded)
const FinalFairnessPostureChart = ({
  data,
  height = 320,
  emptyMessage = 'No fairness posture data.'
}: {
  data: FairnessResultRow[];
  height?: number;
  emptyMessage?: string;
}) => {
  const compReview = data.filter(r => ['UNFAIR', 'MONITOR'].includes(r.status)).length;
  const compResolved = data.filter(r => r.status === 'FAIR').length;
  const auditReview = data.filter(r => ['UNFAIR', 'MONITOR'].includes(r.audit_status)).length;
  const auditResolved = data.filter(r => r.audit_status === 'FAIR').length;
  const excluded = data.filter(r => r.audit_status === 'EXCLUDED').length;

  const chartData = [
    {
      label: 'Review Required',
      compliance: compReview,
      audit: auditReview
    },
    {
      label: 'Resolved',
      compliance: compResolved,
      audit: auditResolved
    },
    {
      label: 'Excluded',
      compliance: 0,
      audit: excluded
    }
  ];

  if (data.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height }}>
        <p className="text-muted mb-0">{emptyMessage}</p>
      </div>
    );
  }

  const compact = height <= 240;
  const margin = compact ? { top: 8, right: 8, left: 0, bottom: 28 } : { top: 20, right: 20, left: 10, bottom: 50 };

  return (
    <div style={{ width: '100%' }}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={chartData} margin={margin}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: '#64748b', fontSize: compact ? 10 : 12 }}
            axisLine={{ stroke: '#e2e8f0' }}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: '#64748b', fontSize: compact ? 10 : 12 }}
            axisLine={false}
            tickLine={false}
            width={compact ? 32 : undefined}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              padding: '12px 16px'
            }}
          />
          <Legend
            wrapperStyle={{ paddingTop: compact ? '4px' : '12px', fontSize: compact ? 11 : 12 }}
            iconType="circle"
          />
          <Bar
            dataKey="compliance"
            name="Compliance"
            fill={CHART_COLORS.complianceBar}
            radius={[6, 6, 0, 0]}
            maxBarSize={compact ? 36 : 48}
          >
            <LabelList dataKey="compliance" position="top" style={{ fontSize: compact ? 9 : 11 }} />
          </Bar>
          <Bar
            dataKey="audit"
            name="Audit"
            fill={CHART_COLORS.auditBar}
            radius={[6, 6, 0, 0]}
            maxBarSize={compact ? 36 : 48}
          >
            <LabelList dataKey="audit" position="top" style={{ fontSize: compact ? 9 : 11 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <p style={{ fontSize: 10, color: '#64748b', marginTop: 4, marginBottom: 0, paddingLeft: 4 }}>
        Compliance = Raw detection. Audit = After reviewer decisions.
      </p>
    </div>
  );
};

/** Summary strip — mirrors Python print_summary; place at top of fairness section. */
export const FairnessSummary = ({ data }: { data: FairnessResultRow[] }) => {
  const total = data.length;
  const reviewRequired = data.filter(r => ['UNFAIR', 'MONITOR'].includes(r.status)).length;
  const excluded = data.filter(r => r.audit_status === 'EXCLUDED').length;
  const resolved = Math.max(0, total - reviewRequired);

  if (total === 0) {
    return null;
  }

  const tiles: {
    label: string;
    value: number;
    hint: string;
    accent: 'slate' | 'amber' | 'teal' | 'rose';
  }[] = [
    {
      label: 'total_attributes',
      value: total,
      hint: 'Rows evaluated in this fairness run.',
      accent: 'slate'
    },
    {
      label: 'review_required',
      value: reviewRequired,
      hint: 'Status UNFAIR or MONITOR — needs attention.',
      accent: 'amber'
    },
    {
      label: 'resolved',
      value: resolved,
      hint: 'Cleared attributes (not in review set).',
      accent: 'teal'
    },
    {
      label: 'excluded',
      value: excluded,
      hint: 'Marked EXCLUDED by audit scope.',
      accent: 'rose'
    }
  ];

  return (
    <div className="fairness-summary-root mb-3">
      <div className="bias-dash">
        <div className="bias-dash-hero fairness-summary-hero">
          <div className="fairness-summary-metrics">
            <Row className="g-2 g-md-2 mt-1">
              {tiles.map(t => (
                <Col xs={12} sm={6} md={3} key={t.label}>
                  <div className="bias-dash-tile bias-dash-tile--compact h-100" data-accent={t.accent}>
                    <div className="bias-dash-tile__label">{t.label}</div>
                    <div className="bias-dash-tile__value">{t.value}</div>
                    <div className="bias-dash-tile__hint">{t.hint}</div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * FairnessPlotsSection — Renders all three fairness plots from Python backend output.
 * Pass `data` as the results array from the fairness script (df.to_dict('records') equivalent).
 */
const DEFAULT_COMPACT_HEIGHT = 220;
const PLOT2_HEIGHT = 200;

export const FairnessPlotsSection: React.FC<FairnessPlotsSectionProps> = ({
  data,
  height = DEFAULT_COMPACT_HEIGHT,
  showSummary = true
}) => {
  const hasData = Array.isArray(data) && data.length > 0;
  const plot2Rows = hasData ? data.filter(row => row.avg_A != null || row.avg_B != null) : [];

  return (
    <div className="fairness-plots-compact">
      {hasData && showSummary && <FairnessSummary data={data} />}

      <Row className="g-2">
        <Col xs={12} lg={6}>
          <Card className="shadow-sm border-0 rounded-4 overflow-visible h-100 bg-white">
            <Card.Body className="p-2">
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#334155', fontSize: '0.9rem' }}>
                Plot 1 — What Needs Review?
              </Typography>
              <WhatNeedsReviewChart data={data} height={height} />
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={6}>
          <Card className="shadow-sm border-0 rounded-4 overflow-visible h-100 bg-white">
            <Card.Body className="p-2">
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#334155', fontSize: '0.9rem' }}>
                Plot 3 — Final Fairness Posture
              </Typography>
              <FinalFairnessPostureChart data={data} height={height} />
            </Card.Body>
          </Card>
        </Col>

        {plot2Rows.map((row, idx) => (
          <Col xs={12} md={6} key={idx}>
            <Card className="shadow-sm border-0 rounded-4 overflow-visible h-100 bg-white">
              <Card.Body className="p-2">
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#334155', fontSize: '0.9rem' }}>
                  Plot 2 — Should I Act? ({row.attribute})
                </Typography>
                <ShouldIActChart row={row} height={PLOT2_HEIGHT} />
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default FairnessPlotsSection;
